import { call, put, select, takeLatest } from 'typed-redux-saga'
import { userApi, UserNS, userSelector, userSlice, UserModel } from 'Pages/user'
import { errorHandler, gridSlice } from 'Modules'
import { Deserialize } from 'cerialize'
import { RequestParams, ResponseCatalog } from 'Models'

function* getAll(action: UserNS.GetAll) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const { params, clear, callback } = action.payload
		const { page, per_page, query, ...fields } = params
		fields.is_deleted = fields.is_deleted ?? false
		const requestParams = new RequestParams(clear ? 1 : page, per_page, query, fields)
		if (clear) {
			yield* put(userSlice.actions.clearAll())
		}
		const res = yield* call(userApi.getAll, requestParams)
		const users = new ResponseCatalog<UserModel>(res)
		users.entries = users.entries.map((item) => Deserialize(item, UserModel))
		if (!clear) {
			const oldUsers = yield* select(userSelector.selectEntries)
			users.entries = oldUsers.concat(res.entries)
		}
		yield* put(userSlice.actions.setAll(users))
		yield* put(userSlice.actions.setRequestParams(requestParams))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getOne(action: UserNS.GetOne) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const res = yield* call(userApi.getOne, action.payload.id)
		const user = Deserialize(res, UserModel)
		yield* put(userSlice.actions.setOne(user))
		if (action.payload.updateGrid) {
			yield* put(userSlice.actions.setOneInGrid(user))
		}
		action.payload.callback?.(res)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getPartner(action: UserNS.GetPartner) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const res = yield* call(userApi.getPartner, action.payload)
		yield* put(userSlice.actions.setPartner(res))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* updateOne(action: UserNS.UpdateOne) {
	try {
		const { id, data } = action.payload
		yield* put(gridSlice.actions.setLoading(true))
		const res = yield* call(userApi.updateOne, id, data)
		const user = Deserialize(res, UserModel)
		yield* put(userSlice.actions.setOne(user))
		if (action.payload.updateGrid) {
			yield* put(userSlice.actions.setOneInGrid(user))
		}
		action.payload.callback?.(res)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* deleteOne(action: UserNS.DeleteOne) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const res = yield* call(userApi.deleteOne, action.payload.id)
		action.payload.callback?.(res)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* resetToken(action: UserNS.ResetToken) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const res = yield* call(userApi.resetToken, action.payload.id)
		action.payload.callback?.(res)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* resendToken(action: UserNS.ResendToken) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const res = yield* call(userApi.resendToken, action.payload.id)
		action.payload.callback?.(res)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* resetPassword(action: UserNS.ResetPassword) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const res = yield* call(userApi.resetPassword, action.payload.id)
		action.payload.callback?.(res)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* completeLeadSocket(action: UserNS.CompleteLeadSocket) {
	try {
		const id = yield* select(userSelector.selectOneId)
		yield* put(gridSlice.actions.setLoading(true))
		if (id) yield* put(userSlice.actions.getOne({ id, updateGrid: true }))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

export const usersSaga = function* saga() {
	yield* takeLatest(userSlice.actions.getAll.type, getAll)
	yield* takeLatest(userSlice.actions.getOne.type, getOne)
	yield* takeLatest(userSlice.actions.getPartner.type, getPartner)
	yield* takeLatest(userSlice.actions.updateOne.type, updateOne)
	yield* takeLatest(userSlice.actions.deleteOne.type, deleteOne)
	yield* takeLatest(userSlice.actions.resetToken.type, resetToken)
	yield* takeLatest(userSlice.actions.resendToken.type, resendToken)
	yield* takeLatest(userSlice.actions.resetPassword.type, resetPassword)
	yield* takeLatest(userSlice.actions.completeLeadSocket.type, completeLeadSocket)
}
